export enum ExerciseType {
  // Single note (pitch) identification: perfect pitch or from a given context
  NOTE_ID = "NOTE_ID",

  // Identify a scale (is it C Major or G Major?), find missing/wrong notes in a scale
  SCALE_ID = "SCALE_ID",
  SCALE_FRAGMENT_ID = "SCALE_FRAGMENT_ID",

  // Identify intervals between two notes (e.g., perfect fifth, major third)
  INTERVAL_ID = "INTERVAL_ID",

  // Identify chords (I, IV, V, vi, etc.) or chord qualities (major, minor, diminished)
  CHORD_ID = "CHORD_ID",

  // Identify chord progressions (e.g., II - V - I) within a given key
  CHORD_PROGRESSION_ID = "CHORD_PROGRESSION_ID",

  // Melody dictation: listen to a short melody and select the correct notes/scale degrees
  MELODY_DICTATION = "MELODY_DICTATION",
  // Variation using scale degrees or number system (e.g., do=1, re=2...)
  MELODY_DICTATION_NUMBER_SYSTEM = "MELODY_DICTATION_NUMBER_SYSTEM",

  // Identify which key a passage is in (C Major vs. G Major, etc.)
  KEY_ID = "KEY_ID",

  // Transposition exercises: verify if a familiar melody is correctly transposed into the current key
  TRANSPOSITION_VALIDATION = "TRANSPOSITION_VALIDATION",

  // Perfect pitch quizzes: identify pitches without a given reference, more advanced and free-form
  PERFECT_PITCH_QUIZ = "PERFECT_PITCH_QUIZ",

  SCALE_ASCENDING_DESCENDING = "SCALE_ASCENDING_DESCENDING",
}

// Points mapping for correct and incorrect answers for each exercise type
export const ExercisePoints = {
  [ExerciseType.NOTE_ID]: {
    correct: 10,
    incorrect: -5, // Deduct 5 points for incorrect answers
  },
  [ExerciseType.SCALE_ID]: {
    correct: 15,
    incorrect: -7,
  },
  [ExerciseType.SCALE_FRAGMENT_ID]: {
    correct: 12,
    incorrect: -6,
  },
  [ExerciseType.INTERVAL_ID]: {
    correct: 15,
    incorrect: -7,
  },
  [ExerciseType.CHORD_ID]: {
    correct: 15,
    incorrect: -7,
  },
  [ExerciseType.CHORD_PROGRESSION_ID]: {
    correct: 25,
    incorrect: -10,
  },
  [ExerciseType.MELODY_DICTATION]: {
    correct: 30,
    incorrect: -12,
  },
  [ExerciseType.MELODY_DICTATION_NUMBER_SYSTEM]: {
    correct: 25,
    incorrect: -10,
  },
  [ExerciseType.KEY_ID]: {
    correct: 15,
    incorrect: -5,
  },
  [ExerciseType.TRANSPOSITION_VALIDATION]: {
    correct: 25,
    incorrect: -10,
  },
  [ExerciseType.PERFECT_PITCH_QUIZ]: {
    correct: 30,
    incorrect: -15,
  },
  [ExerciseType.SCALE_ASCENDING_DESCENDING]: {
    correct: 10,
    incorrect: -5,
  },
};

export interface MultipleChoiceOption {
  label: string;
  midiNumbers?: number[];
}

export interface ExerciseDefinition {
  id: string; // Unique ID for the exercise instance
  type: ExerciseType; // The category/type of the exercise
  keyContext?: string; // The key signature relevant to the exercise (e.g., "C Major"), optional if not needed
  difficulty: number; // A numeric level to indicate complexity or user’s progression
  data: ExerciseData; // Additional parameters depending on the exercise type (e.g., notes array, audio references)
}

export interface ExerciseData {
  notes?: number[];
  correctAnswer?: string; // For multiple choice, the correct answer
  choices?: MultipleChoiceOption[]; // For multiple choice options
  uiMode?: "PIANO" | "MULTIPLE_CHOICE";
  promptText?: string;
}

/*
const renderExercise = (exercise: ExerciseDefinition) => {
  switch (exercise.type) {
    case ExerciseType.NOTE_ID:
      return (
        <NoteIdentification
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.INTERVAL_ID:
      return (
        <IntervalIdentification
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.CHORD_ID:
      return (
        <ChordIdentification
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.MELODY_DICTATION:
      return (
        <MelodyDictation
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.SCALE_FRAGMENT_ID:
      return (
        <ScaleFragmentIdentification
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.CHORD_PROGRESSION_ID:
      return (
        <ChordProgressionIdentification
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    case ExerciseType.PERFECT_PITCH_QUIZ:
      return (
        <PerfectPitchQuiz
          exercise={exercise}
          ref={currentExerciseRef}
          onResult={handleExerciseResult}
        />
      );
    default:
      return <UnsupportedExercise exercise={exercise} />;
  }
};
*/
